import CampaignEligibleLeadSelect from './CampaignEligibleLeadSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import WhatsAppConnectionInsights from './WhatsAppConnectionInsights';
import WhatsAppCostEstimate from './WhatsAppCostEstimate';
import TemplateParameterMapper from './TemplateParameterMapper';
import './WhatsAppCampaigns.css';

const parameterPreviewValues = {
  '{{nome}}': 'Maria Silva',
  '{{interesse}}': 'Curso de Ensino Medio',
  '{{telefone}}': '(11) 99999-9999',
  '{{email}}': 'maria@exemplo.com',
  '{{cidade}}': 'Sao Paulo',
  '{{empresa}}': 'Empresa Exemplo',
};

function renderTemplatePreview(message, parameters = []) {
  let index = 0;
  return (message || '').replace(/{{\s*[^{}]+?\s*}}/g, placeholder => {
    const source = parameters[index++];
    if (!source || source === '__fixed__') return placeholder;
    return parameterPreviewValues[source] || source;
  });
}

function CampaignPhonePreview({ message, label = 'PREVIA NO WHATSAPP' }) {
  const longMessage = (message || '').length > 420;
  return <div className="campaign-phone-preview">
    <div className="campaign-phone-preview__label">{label}</div>
    <div className="campaign-phone">
      <div className="campaign-phone__speaker" />
      <div className="campaign-phone__header">
        <span className="campaign-phone__back">{'<'}</span>
        <span className="campaign-phone__avatar">FC</span>
        <span><strong>Sua empresa</strong><small>conta comercial</small></span>
      </div>
      <div className={'campaign-phone__chat' + (longMessage ? ' has-long-message' : '')}
        tabIndex={longMessage ? 0 : undefined}
        aria-label={longMessage ? 'Mensagem longa. Role para visualizar o conteudo completo.' : undefined}>
        <div className="campaign-phone__day">HOJE</div>
        <div className="campaign-phone__bubble">
          <div>{message || 'Selecione um template para visualizar a mensagem.'}</div>
          <span>10:30 <b>{'\u2713\u2713'}</b></span>
        </div>
      </div>
      <div className="campaign-phone__composer"><span>+</span><div>Mensagem</div><b>o</b></div>
    </div>
    {longMessage && <div className="campaign-phone-preview__scroll-hint">
      <span>↕</span> Role dentro do celular para ler a mensagem completa
    </div>}
    <small>A aparencia final pode variar conforme o aparelho.</small>
  </div>;
}

const emptyForm = {
  id: null, name: '', connection_id: '', message: '',
  filters: { interest: '', pipeline_id: '', stage_id: '', tag: '', only_unconverted: true },
  settings: { interval_seconds: 10, max_per_day: 500, window_start: '08:00', window_end: '20:00',
    template_name: '', template_language: '', template_category: '', template_parameters: [] },
};
const statusLabels = { draft: 'Rascunho', scheduled: 'Agendada', running: 'Em envio', paused: 'Pausada',
  test_completed: 'Teste concluído', approved: 'Teste aprovado', completed: 'Concluída', cancelled: 'Cancelada' };

export default function WhatsAppCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [support, setSupport] = useState({ connections: [], pipelines: [], interests: [] });
  const [metrics, setMetrics] = useState({ sent: 0, replied: 0, converted: 0, failed: 0 });
  const [details, setDetails] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [audienceBusy, setAudienceBusy] = useState(false);
  const [consentNotice, setConsentNotice] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [execution, setExecution] = useState({ mode: 'test', test_lead_id: '', scheduled_at: '', confirm: false });
  const [error, setError] = useState('');
  const messageInput = useRef(null);
  const insertMessageVariable = variable => {
    const input = messageInput.current;
    if (!input) return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const value = input.value.slice(0, start) + variable + input.value.slice(end);
    if (value.length > 3000) { setError('A mensagem pode ter no máximo 3000 caracteres.'); return; }
    setForm(current => ({ ...current, message: value }));
    requestAnimationFrame(() => { input.focus(); input.setSelectionRange(start + variable.length, start + variable.length); });
  };
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const startNewCampaign = () => {
    setForm({
      ...emptyForm,
      filters: { ...emptyForm.filters },
      settings: { ...emptyForm.settings, template_parameters: [], template_parameter_names: [] },
    });
    setPreview(null);
    setDetails(null);
    setExecution({ mode: 'test', test_lead_id: '', scheduled_at: '', confirm: false });
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/crm/campaigns');
      setCampaigns(response.data?.campaigns || []);
      setSupport(response.data?.support || { connections: [], pipelines: [], interests: [] });
      setMetrics(response.data?.metrics || { sent: 0, replied: 0, converted: 0, failed: 0 });
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Não foi possível carregar as campanhas.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const hasActiveCampaigns = campaigns.some(item => ['scheduled', 'running'].includes(item.status));
  useEffect(() => {
    if (!hasActiveCampaigns) return undefined;
    const timer = window.setInterval(load, 5000);
    return () => window.clearInterval(timer);
  }, [hasActiveCampaigns, load]);

  const pipeline = useMemo(() => support.pipelines.find(
    item => String(item.id) === String(form.filters.pipeline_id)
  ), [support.pipelines, form.filters.pipeline_id]);
  const selectedConnection = useMemo(() => support.connections.find(
    item => String(item.id) === String(form.connection_id)
  ), [support.connections, form.connection_id]);
  const officialConnection = selectedConnection?.provider === 'cloud_api';
  const connectionInsights = selectedConnection?.insights;

  useEffect(() => {
    if (!officialConnection || !form.connection_id) {
      setTemplates([]);
      return;
    }
    let active = true;
    setLoadingTemplates(true);
    axios.get(`/api/connections/${form.connection_id}/templates`)
      .then(response => { if (active) setTemplates(response.data?.templates || []); })
      .catch(requestError => {
        if (active) setError(requestError.response?.data?.message || 'Não foi possível carregar os templates aprovados.');
      })
      .finally(() => { if (active) setLoadingTemplates(false); });
    return () => { active = false; };
  }, [officialConnection, form.connection_id]);

  const selectedTemplate = useMemo(() => templates.find(item =>
    item.name === form.settings.template_name && item.language === form.settings.template_language
  ), [templates, form.settings.template_name, form.settings.template_language]);
  const templateBody = useMemo(() => (
    selectedTemplate?.components?.find(component => component.type === 'BODY')?.text || ''
  ), [selectedTemplate]);
  const templatePlaceholders = useMemo(() => [...templateBody.matchAll(/{{\s*([^{}]+?)\s*}}/g)].map(match => match[1]), [templateBody]);
  const templatePreviewMessage = useMemo(() => renderTemplatePreview(
    templateBody,
    form.settings.template_parameters || []
  ), [templateBody, form.settings.template_parameters]);

  const changeFilter = (key, value) => setForm(current => ({
    ...current, filters: { ...current.filters, [key]: value },
  }));

  const saveAndPreview = async event => {
    event.preventDefault();
    const configuredParameters = form.settings.template_parameters || [];
    const numericPlaceholders = templatePlaceholders.length > 0
      && templatePlaceholders.every(value => /^\d+$/.test(value));
    const requiredParameterCount = numericPlaceholders
      ? Math.max(...templatePlaceholders.map(Number))
      : templatePlaceholders.length;
    if (officialConnection && requiredParameterCount > 0
      && (configuredParameters.length !== requiredParameterCount
        || configuredParameters.some(value =>
          !String(value || '').trim() || value === '__fixed__'
        ))) {
      setError('Associe ou preencha todas as variaveis do template antes de salvar a campanha.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        connection_id: Number(form.connection_id),
        filters: {
          ...form.filters,
          pipeline_id: form.filters.pipeline_id ? Number(form.filters.pipeline_id) : null,
          stage_id: form.filters.stage_id ? Number(form.filters.stage_id) : null,
        },
      };
      const saved = form.id
        ? await axios.put(`/api/crm/campaigns/${form.id}`, payload)
        : await axios.post('/api/crm/campaigns', payload);
      const campaign = saved.data.campaign;
      setForm(current => ({ ...current, id: campaign.id }));
      const result = await axios.post(`/api/crm/campaigns/${campaign.id}/preview`);
      setPreview(result.data);
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message
        || Object.values(requestError.response?.data?.errors || {})[0]?.[0]
        || 'Não foi possível salvar a campanha.');
    } finally {
      setSaving(false);
    }
  };

  const edit = campaign => {
    setForm({
      id: campaign.id, name: campaign.name, connection_id: campaign.connection_id,
      message: campaign.message, filters: { ...emptyForm.filters, ...(campaign.filters || {}) },
      settings: { ...emptyForm.settings, ...(campaign.settings || {}) },
    });
    setPreview(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadAudiencePage = async page => {
    setAudienceBusy(true); setError('');
    try {
      const result = await axios.post(`/api/crm/campaigns/${preview.campaign.id}/preview`, { page });
      setPreview(result.data);
    } catch (e) { setError(e.response?.data?.message || 'Não foi possível carregar a audiência.'); }
    finally { setAudienceBusy(false); }
  };
  const registerAudienceConsent = async () => {
    const source = window.prompt('Onde e quando TODOS os contatos desta audiência autorizaram mensagens? Informe a origem do consentimento (5 a 100 caracteres).');
    if (!source?.trim()) return;
    if (!window.confirm(`Registrar consentimento na audiência salva de ${preview.summary.total} leads, incluindo todas as páginas? Confirme somente se já possui a autorização desses contatos. Descadastros, Não perturbe e consentimentos existentes serão preservados.`)) return;
    setAudienceBusy(true); setError(''); setConsentNotice('');
    try {
      const result = await axios.post(`/api/crm/campaigns/${preview.campaign.id}/consent`, { source: source.trim(), confirm: true });
      setConsentNotice(`${result.data.registered} consentimentos registrados; ${result.data.skipped} registros preservados ou ignorados.`);
      const refreshed = await axios.post(`/api/crm/campaigns/${preview.campaign.id}/preview`, { page: preview.pagination?.current_page || 1 });
      setPreview(refreshed.data);
    } catch (e) { setError(Object.values(e.response?.data?.errors || {}).flat().join(' ') || e.response?.data?.message || 'Não foi possível registrar o lote. Atualize a prévia antes de tentar novamente.'); }
    finally { setAudienceBusy(false); }
  };

  const removeAudienceLead = async recipient => {
    if (!window.confirm(`Remover ${recipient.name} somente da audiência desta campanha? O cadastro no CRM será mantido.`)) return;
    setAudienceBusy(true); setError('');
    try {
      await axios.post(`/api/crm/campaigns/${preview.campaign.id}/exclude/${recipient.id}`, {});
      if (String(execution.test_lead_id) === String(recipient.id)) setExecution(current => ({ ...current, test_lead_id: '' }));
      const page = Math.max(1, Math.min(preview.pagination?.current_page || 1, Math.ceil((preview.summary.total - 1) / (preview.pagination?.per_page || 100))));
      const result = await axios.post(`/api/crm/campaigns/${preview.campaign.id}/preview`, { page });
      setPreview(result.data);
    } catch (e) { setError(e.response?.data?.message || 'Não foi possível remover o lead.'); }
    finally { setAudienceBusy(false); }
  };

  const registerConsent = async recipient => {
    const source = window.prompt(
      'Informe onde e como o consentimento foi obtido (ex.: formulário do site em 07/09/2026):'
    );
    if (!source?.trim()) return;
    try {
      await axios.post(`/api/crm/leads/${recipient.id}/whatsapp-consent`, { source: source.trim() });
      const result = await axios.post(`/api/crm/campaigns/${form.id}/preview`);
      setPreview(result.data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Não foi possível registrar o consentimento.');
    }
  };

  const schedule = async () => {
    if (!form.id || !execution.confirm) return;
    setExecuting(true); setError('');
    try {
      const response = await axios.post(`/api/crm/campaigns/${form.id}/schedule`, {
        ...execution,
        test_lead_id: execution.mode === 'test' ? Number(execution.test_lead_id) : null,
        scheduled_at: execution.scheduled_at || null,
      });
      setPreview(current => ({ ...current, campaign: response.data.campaign }));
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Não foi possível agendar a campanha.');
    } finally { setExecuting(false); }
  };

  const command = async (campaign, action) => {
    setExecuting(true); setError('');
    try { await axios.post(`/api/crm/campaigns/${campaign.id}/${action}`); await load(); }
    catch (requestError) { setError(requestError.response?.data?.message || 'Não foi possível alterar a campanha.'); }
    finally { setExecuting(false); }
  };
  const removeCampaign = async campaign => {
    if (!window.confirm('Excluir definitivamente a campanha "' + campaign.name + '" e o historico de destinatarios?')) return;
    setExecuting(true); setError('');
    try {
      await axios.delete('/api/crm/campaigns/' + campaign.id);
      if (String(form.id) === String(campaign.id)) startNewCampaign();
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Nao foi possivel excluir a campanha.');
    } finally { setExecuting(false); }
  };
  const approveTest = async campaign => {
    setExecuting(true); setError('');
    try {
      const response = await axios.post(`/api/crm/campaigns/${campaign.id}/approve-test`);
      setPreview(current => current && form.id === campaign.id ? { ...current, campaign: response.data.campaign } : current);
      await load();
    } catch (requestError) { setError(requestError.response?.data?.message || 'Não foi possível aprovar o teste.'); }
    finally { setExecuting(false); }
  };
  const viewCampaign = async campaign => {
    setForm({ id: campaign.id, name: campaign.name, connection_id: campaign.connection_id,
      message: campaign.message, filters: { ...emptyForm.filters, ...(campaign.filters || {}) },
      settings: { ...emptyForm.settings, ...(campaign.settings || {}) } });
    setExecution({ mode: 'bulk', test_lead_id: '', scheduled_at: '', confirm: false });
    setError('');
    const response = await axios.post(`/api/crm/campaigns/${campaign.id}/preview`);
    setPreview(response.data); window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const showDetails = async campaign => {
    try { const response = await axios.get(`/api/crm/campaigns/${campaign.id}/recipients`); setDetails({ campaign, ...response.data }); }
    catch (requestError) { setError(requestError.response?.data?.message || 'Não foi possível carregar os destinatários.'); }
  };

  if (loading && !campaigns.length) return <div className="text-center p-5"><Spinner animation="border" /></div>;
  return (
    <div style={{ padding: 24, maxWidth: 1500, margin: '0 auto' }}>
      <div className="mb-4 d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div><h2 className="h5 mb-1">Campanhas de WhatsApp</h2>
        <div className="text-muted small">Crie o rascunho, segmente e valide a audiência antes de liberar o disparo.</div></div>
        <Button variant="success" onClick={startNewCampaign}>+ Nova campanha</Button>
      </div>
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      <Alert variant="info">
        Faça primeiro um envio de teste. O envio completo exige consentimento registrado e confirmação explícita antes de entrar na fila.
      </Alert>
      <div className="row g-3 mb-4">
        {[['Enviadas', metrics.sent, 'primary'], ['Respostas', metrics.replied, 'info'],
          ['Conversões', metrics.converted, 'success'], ['Falhas', metrics.failed, 'danger']].map(([label, value, color]) =>
          <div className="col-6 col-md-3" key={label}><div className={`border rounded p-3 bg-white border-${color}`}>
            <div className="text-muted small">{label}</div><strong className="h4 mb-0">{value}</strong>
          </div></div>)}
      </div>

      {officialConnection && connectionInsights && <div className={'mb-4'}>
        <WhatsAppConnectionInsights data={connectionInsights} />
      </div>}
      <form onSubmit={saveAndPreview} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
        <div className="row g-3">
          <div className="col-md-6"><label className="form-label" htmlFor="campaign-name">Nome da campanha</label>
            <input id="campaign-name" className="form-control" required maxLength={150} value={form.name}
              onChange={event => setForm(current => ({ ...current, name: event.target.value }))} /></div>
          <div className="col-md-6"><label className="form-label" htmlFor="campaign-connection">Conexão WhatsApp</label>
            <select id="campaign-connection" className="form-select" required value={form.connection_id}
              onChange={event => setForm(current => ({ ...current, connection_id: event.target.value }))}>
              <option value="">Selecione</option>
              {support.connections.map(item => <option key={item.id} value={item.id}>
                {item.name} · {item.provider} · {item.status}
              </option>)}
            </select></div>
          {officialConnection && <div className="col-12 border rounded bg-light p-3 campaign-template-panel">
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label" htmlFor="campaign-template">Template aprovado na Meta</label>
                <select id="campaign-template" className="form-select" required
                  disabled={loadingTemplates} value={form.settings.template_name && form.settings.template_language
                    ? `${form.settings.template_name}|${form.settings.template_language}` : ''}
                  onChange={event => {
                    const [name, language] = event.target.value.split('|');
                    const template = templates.find(item => item.name === name && item.language === language);
                    const body = template?.components?.find(component => component.type === 'BODY')?.text || '';
                    const placeholders = [...body.matchAll(/{{\s*([^{}]+?)\s*}}/g)].map(match => match[1]);
                    const numeric = placeholders.every(value => /^\d+$/.test(value));
                    const count = numeric && placeholders.length ? Math.max(...placeholders.map(Number)) : placeholders.length;
                    const parameterNames = numeric ? [] : placeholders;
                    setForm(current => ({ ...current, message: body, settings: { ...current.settings,
                      template_name: name || '', template_language: language || '',
                      template_category: template?.category || '', template_parameter_names: parameterNames,
                      template_parameters: Array.from({ length: count }, (_, index) =>
                        current.settings.template_parameters?.[index] || '') } }));
                  }}>
                  <option value="">{loadingTemplates ? 'Carregando...' : 'Selecione um template'}</option>
                  {templates.map(item => <option key={`${item.name}|${item.language}`}
                    value={`${item.name}|${item.language}`}>{item.name} · {item.language} · {item.category}</option>)}
                </select>
              </div>
              <TemplateParameterMapper className="col-lg-7" placeholders={templatePlaceholders} values={form.settings.template_parameters || []} onChange={parameters => setForm(current => ({ ...current, settings: { ...current.settings, template_parameters: parameters } }))} />
              {templateBody && <div className="col-lg-5"><CampaignPhonePreview message={templatePreviewMessage} /></div>}
              {templateBody && <div className="col-12"><small className="text-muted">Conteúdo aprovado</small>
                <div style={{ whiteSpace: 'pre-wrap' }}>{templateBody}</div></div>}
            </div>
          </div>}
          {!officialConnection && <div className="col-12"><label className="form-label" htmlFor="campaign-message">Mensagem</label>
            <textarea ref={messageInput} id="campaign-message" className="form-control" rows={4} required maxLength={3000}
              readOnly={officialConnection} value={form.message}
              onChange={event => setForm(current => ({ ...current, message: event.target.value }))}
              placeholder={officialConnection ? 'Selecione um template aprovado acima.' : 'Olá, {{nome}}! Temos uma novidade para você...'} />
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2" role="group" aria-label="Personalizar mensagem">
              <span className="small text-muted">Inserir variável:</span>
              <Button type="button" size="sm" variant="outline-primary" onClick={() => insertMessageVariable('{{nome}}')}>Nome do lead</Button>
              <Button type="button" size="sm" variant="outline-primary" onClick={() => insertMessageVariable('{{interesse}}')}>Interesse do lead</Button>
            </div>
            <div className="form-text">As variáveis serão substituídas pelos dados de cada destinatário. Campos sem informação ficam vazios.</div>
            <div className="form-text">{officialConnection
              ? 'Na API Oficial, o conteúdo é definido pelo template aprovado na Meta.'
              : 'O texto de descadastro “responda SAIR” será incluído automaticamente.'}</div>
          </div>}
          <div className="col-md-3"><label className="form-label">Interesse</label>
            <select className="form-select" value={form.filters.interest}
              onChange={event => changeFilter('interest', event.target.value)}>
              <option value="">Todos</option>{support.interests.map(item => <option key={item}>{item}</option>)}
            </select></div>
          <div className="col-md-3"><label className="form-label">Pipeline</label>
            <select className="form-select" value={form.filters.pipeline_id}
              onChange={event => setForm(current => ({
                ...current,
                filters: { ...current.filters, pipeline_id: event.target.value, stage_id: '' },
              }))}>
              <option value="">Todas</option>{support.pipelines.map(item => <option key={item.id} value={item.id}>{item.nome}</option>)}
            </select></div>
          <div className="col-md-3"><label className="form-label">Etapa</label>
            <select className="form-select" value={form.filters.stage_id}
              onChange={event => changeFilter('stage_id', event.target.value)} disabled={!pipeline}>
              <option value="">Todas</option>{(pipeline?.estagios || []).map(item => <option key={item.id} value={item.id}>{item.nome}</option>)}
            </select></div>
          <div className="col-md-3"><label className="form-label">Tag</label>
            <input className="form-control" value={form.filters.tag}
              onChange={event => changeFilter('tag', event.target.value)} /></div>
          <div className="col-md-3"><label className="form-label">Intervalo entre mensagens</label>
            <input type="number" min="6" max="3600" className="form-control" value={form.settings.interval_seconds}
              onChange={event => setForm(current => ({ ...current, settings: { ...current.settings, interval_seconds: Number(event.target.value) } }))} /></div>
          <div className="col-md-3"><label className="form-label">Limite diário</label>
            <input type="number" min="1" max="1000" className="form-control" value={form.settings.max_per_day}
              onChange={event => setForm(current => ({ ...current, settings: { ...current.settings, max_per_day: Number(event.target.value) } }))} /></div>
          <div className="col-md-3"><label className="form-label">Início da janela</label>
            <input type="time" className="form-control" value={form.settings.window_start}
              onChange={event => setForm(current => ({ ...current, settings: { ...current.settings, window_start: event.target.value } }))} /></div>
          <div className="col-md-3"><label className="form-label">Fim da janela</label>
            <input type="time" className="form-control" value={form.settings.window_end}
              onChange={event => setForm(current => ({ ...current, settings: { ...current.settings, window_end: event.target.value } }))} /></div>
          <div className="col-12 d-flex justify-content-between align-items-center">
            <label className="d-flex gap-2 align-items-center"><input type="checkbox"
              checked={form.filters.only_unconverted}
              onChange={event => changeFilter('only_unconverted', event.target.checked)} />
              Somente leads não convertidos</label>
            <div className="d-flex gap-2">
              {form.id && <Button type="button" variant="outline-secondary"
                onClick={startNewCampaign}>Descartar e criar nova</Button>}
              {preview?.campaign?.status === 'approved'
                ? <Badge bg="success" className="d-flex align-items-center px-3">Campanha aprovada para envio</Badge>
                : <Button type="submit" disabled={saving}>{saving ? 'Processando...' : 'Salvar e visualizar audiência'}</Button>}
            </div>
          </div>
        </div>
      </form>

      {preview && <Modal show onHide={() => { if (!audienceBusy && !executing) setPreview(null); }} size="xl" centered scrollable fullscreen="md-down" backdrop={audienceBusy || executing ? 'static' : true} keyboard={!audienceBusy && !executing} aria-labelledby="campaign-audience-title">
        <Modal.Header closeButton={!audienceBusy && !executing}><Modal.Title id="campaign-audience-title">Audiência · {preview.campaign?.name || form.name}</Modal.Title></Modal.Header>
        <Modal.Body className="p-0">
        {error && <Alert variant="danger" className="m-3">{error}</Alert>}
        <div className="p-3 border-bottom d-flex flex-wrap gap-3">
          <strong>Prévia da audiência</strong><Badge bg="secondary">Total {preview.summary.total}</Badge>
          <Badge bg="success">Elegíveis {preview.summary.eligible}</Badge>
          <Badge bg="danger">Excluídos {preview.summary.excluded}</Badge>
        </div>
        <div className="p-3 border-bottom"><Button variant="outline-primary" disabled={audienceBusy} onClick={registerAudienceConsent}>Registrar consentimento da audiência inteira</Button><p className="small text-muted mt-2 mb-0">Considera os filtros salvos da campanha e todas as páginas. Registre apenas autorizações já obtidas. Contatos descadastrados não serão reativados.</p>{consentNotice && <Alert variant="success" className="mt-2 mb-0">{consentNotice}</Alert>}</div>
        <WhatsAppCostEstimate data={preview.cost_estimate} />
        <div className={'campaign-final-preview-old'}><small className={'text-muted'}>Mensagem final</small>
          <div style={{ whiteSpace: 'pre-wrap' }}>{preview.effective_message}</div></div>
        <div className="campaign-final-preview">
          <CampaignPhonePreview message={preview.effective_message} label="MENSAGEM FINAL DA CAMPANHA" />
        </div>
        <div className="table-responsive"><table className="table table-hover mb-0">
          <thead><tr><th>Lead</th><th>Telefone</th><th>Interesse</th><th>Situação</th><th /></tr></thead>
          <tbody>{preview.recipients.map(item => <tr key={item.id}>
            <td>{item.name}</td><td>{item.phone || '—'}</td><td>{item.interest || '—'}</td>
            <td>{item.eligible ? <Badge bg="success">Elegível</Badge>
              : <Badge bg="secondary">{item.excluded_reason}</Badge>}</td>
            <td>{item.excluded_reason === 'Sem consentimento' && <Button size="sm" variant="outline-primary"
              disabled={audienceBusy} onClick={() => registerConsent(item)}>Registrar consentimento</Button>}{['draft', 'test_completed', 'approved'].includes(preview.campaign?.status) && <Button className="ms-2" size="sm" variant="outline-danger" disabled={audienceBusy} onClick={() => removeAudienceLead(item)}>Remover da audiência</Button>}</td>
          </tr>)}</tbody>
        </table></div>
        {preview.pagination && <div className="d-flex flex-wrap align-items-center gap-3 p-3 border-top"><Button variant="light" disabled={audienceBusy || preview.pagination.current_page <= 1} onClick={() => loadAudiencePage(preview.pagination.current_page - 1)}>Página anterior</Button><span>Página {preview.pagination.current_page} de {preview.pagination.last_page} · {preview.pagination.total} leads</span><Button variant="light" disabled={audienceBusy || preview.pagination.current_page >= preview.pagination.last_page} onClick={() => loadAudiencePage(preview.pagination.current_page + 1)}>Próxima página</Button></div>}
        {['draft', 'test_completed', 'approved'].includes(preview.campaign?.status) && <div className="p-3 border-top">
          <h3 className="h6">{preview.campaign?.status === 'approved' ? 'Enviar campanha completa' : 'Programar execução'}</h3>
          {preview.campaign?.status === 'approved' && <Alert variant="success" className="py-2">
            O teste foi aprovado. Confirme abaixo para enviar aos {preview.summary.eligible} leads elegíveis.
          </Alert>}
          <div className="row g-3 align-items-end">
            <div className="col-md-3"><label className="form-label" htmlFor="campaign-mode">Modo</label>
              <select id="campaign-mode" className="form-select" value={execution.mode}
                disabled={preview.campaign?.status === 'approved'}
                onChange={event => setExecution(current => ({ ...current, mode: event.target.value, test_lead_id: '' }))}>
                {preview.campaign?.status !== 'approved' && <option value="test">Teste: somente 1 lead</option>}
                {preview.campaign?.status === 'approved' && <option value="bulk">Campanha completa</option>}
              </select></div>
            {execution.mode === 'test' && <div className="col-md-4"><label className="form-label" htmlFor="campaign-test-lead">Destinatário do teste</label>
              <CampaignEligibleLeadSelect key={preview.campaign.id} campaignId={preview.campaign.id} recipients={preview.recipients} value={execution.test_lead_id} onChange={id => setExecution(current => ({ ...current, test_lead_id: id }))} /></div>}
            <div className="col-md-3"><label className="form-label" htmlFor="campaign-scheduled-at">Data e hora (opcional)</label>
              <input id="campaign-scheduled-at" type="datetime-local" className="form-control" value={execution.scheduled_at}
                onChange={event => setExecution(current => ({ ...current, scheduled_at: event.target.value }))} /></div>
            <div className="col-12"><label className="d-flex gap-2 align-items-start">
              <input type="checkbox" checked={execution.confirm}
                onChange={event => setExecution(current => ({ ...current, confirm: event.target.checked }))} />
              <span>Confirmo que estes contatos autorizaram mensagens de marketing e revisei o conteúdo e a audiência.</span>
            </label></div>
            <div className="col-12"><Button variant={execution.mode === 'bulk' ? 'danger' : 'primary'}
              disabled={executing || !execution.confirm || (execution.mode === 'test' && !execution.test_lead_id)} onClick={schedule}>
              {executing ? 'Agendando...' : execution.mode === 'test' ? 'Agendar envio de teste' : `Agendar campanha para ${preview.summary.eligible} leads`}
            </Button></div>
          </div>
        </div>}
        </Modal.Body><Modal.Footer><Button variant="secondary" disabled={audienceBusy || executing} onClick={() => setPreview(null)}>Fechar audiência</Button></Modal.Footer>
      </Modal>}

      <div className="mt-4"><h3 className="h6">Campanhas</h3>
        {campaigns.length === 0 ? <div className="text-muted">Nenhuma campanha criada.</div>
          : campaigns.map(item => <div key={item.id} className="bg-white border rounded p-3 mb-2 d-flex justify-content-between align-items-center">
            <div><strong>{item.name}</strong><div className="text-muted small">
              {item.connection?.name} · <Badge bg="secondary">{statusLabels[item.status] || item.status}</Badge> · {item.eligible_count} elegíveis · {item.sent_count || 0} enviados · {item.failed_count || 0} falhas
            </div></div>
            <div className="d-flex gap-2">
              {['draft', 'test_completed'].includes(item.status) && <Button size="sm" variant="outline-primary" onClick={() => edit(item)}>Editar</Button>}
              {item.status === 'test_completed' && <Button size="sm" variant="success" disabled={executing} onClick={() => approveTest(item)}>Aprovar teste</Button>}
              {item.status === 'approved' && <Button size="sm" variant="primary" onClick={() => viewCampaign(item)}>Revisar e enviar</Button>}
              {item.recipients_count > 0 && <Button size="sm" variant="outline-secondary" onClick={() => showDetails(item)}>Destinatários</Button>}
              {['scheduled', 'running'].includes(item.status) && <Button size="sm" variant="outline-warning" disabled={executing} onClick={() => command(item, 'pause')}>Pausar</Button>}
              {item.status === 'paused' && <Button size="sm" variant="outline-success" disabled={executing} onClick={() => command(item, 'resume')}>Continuar</Button>}
              {['scheduled', 'running', 'paused'].includes(item.status) && <Button size="sm" variant="outline-danger" disabled={executing} onClick={() => command(item, 'cancel')}>Cancelar</Button>}
              {!['scheduled', 'running', 'paused'].includes(item.status) && <Button size="sm" variant="outline-danger" disabled={executing} onClick={() => removeCampaign(item)}>Excluir</Button>}
            </div>
          </div>)}
      </div>
      {details && <div className="mt-4 bg-white border rounded">
        <div className="p-3 border-bottom d-flex justify-content-between"><strong>Destinatários · {details.campaign.name}</strong>
          <Button size="sm" variant="outline-secondary" onClick={() => setDetails(null)}>Fechar</Button></div>
        <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr>
          <th>Lead</th><th>Telefone</th><th>Status</th><th>Tentativas</th><th>Enviado</th><th>Resposta</th><th>Conversão</th><th>Erro</th>
        </tr></thead><tbody>{(details.data || []).map(item => <tr key={item.id}>
          <td>{item.lead_name}</td><td>{item.phone}</td><td>{item.status}</td><td>{item.attempts}</td>
          <td>{item.sent_at ? new Date(item.sent_at).toLocaleString('pt-BR') : '—'}</td>
          <td>{item.replied_at ? 'Sim' : 'Não'}</td><td>{item.converted_at ? 'Sim' : 'Não'}</td><td>{item.last_error || '—'}</td>
        </tr>)}</tbody></table></div>
      </div>}
    </div>
  );
}
